<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import ShowsManager from './ShowsManager.vue';
import MusicManager from './MusicManager.vue';
import PhotosManager from './PhotosManager.vue';
import SettingsManager from './SettingsManager.vue';
import AboutManager from './AboutManager.vue';
import UsersManager from './UsersManager.vue';
import ImprintManager from './ImprintManager.vue';
import PrivacyManager from './PrivacyManager.vue';
import VideoManager from './VideoManager.vue';
import StatsManager from './StatsManager.vue';
import MaintenanceManager from './MaintenanceManager.vue';
import AdminFanPhotos from '../AdminFanPhotos.vue';
import FanPhotoQRCode from '../FanPhotoQRCode.vue';
import WatermarkManager from './WatermarkManager.vue';

// Aktiver Menüpunkt
const activeTab = ref('shows');
// Unterpunkt für Rechtliches (imprint oder privacy)
const legalSubTab = ref('imprint');
// Unterpunkt für Media (music, photos oder videos)
const mediaSubTab = ref('music');
// Status für ausgeklapptes Rechtliches-Menü
const isLegalMenuOpen = ref(false);
// Status für ausgeklapptes Media-Menü
const isMediaMenuOpen = ref(false);
// Dunkler Modus
const darkMode = ref(false);
// Mobiles Menü Status
const mobileMenuOpen = ref(false);
// Anzahl der ausstehenden Fan-Fotos
const pendingFanPhotos = ref(0);

// Benutzername aus dem localStorage oder einer anderen Quelle
const username = ref('');

// Benutzernamen beim Laden der Komponente abrufen
const fetchUsername = async () => {
  try {
    const response = await fetch('/api/check_admin.php', {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    
    const data = await response.json();
    if (data.success && data.user && data.user.username) {
      username.value = data.user.username;
    }
  } catch (error) {
    console.error('Fehler beim Abrufen des Benutzernamens:', error);
  }
};

// Dynamische Begrüßung basierend auf der Tageszeit
const greeting = computed(() => {
  const hour = new Date().getHours();
  
  if (hour >= 5 && hour < 12) {
    return 'Guten Morgen';
  } else if (hour >= 12 && hour < 18) {
    return 'Guten Tag';
  } else if (hour >= 18 && hour < 22) {
    return 'Guten Abend';
  } else {
    return 'Gute Nacht';
  }
});

// Beim Initialisieren prüfen, ob ein gespeichertes Theme vorhanden ist und ausstehende Fan-Fotos abrufen
onMounted(() => {
  const savedTheme = localStorage.getItem('admin-theme');
  if (savedTheme === 'dark') {
    darkMode.value = true;
    document.body.classList.add('dark-mode');
  }

  // Event-Listener für Fensterbreite
  window.addEventListener('resize', checkWindowSize);
  checkWindowSize();
  
  // Ausstehende Fan-Fotos abrufen
  fetchPendingFanPhotos();
  
  // Benutzernamen abrufen
  fetchUsername();
  
  // Timer für regelmäßige Aktualisierung der ausstehenden Fan-Fotos (alle 5 Minuten)
  setInterval(fetchPendingFanPhotos, 5 * 60 * 1000);
});

// Fan-Fotos, die noch nicht freigegeben wurden, abrufen
const fetchPendingFanPhotos = async () => {
  try {
    const params = new URLSearchParams();
    params.append('approved', '0'); // Nur nicht freigegebene Fotos
    
    const response = await fetch(`/api/admin_fan_photos.php?${params.toString()}`);
    const data = await response.json();
    
    if (data.success) {
      pendingFanPhotos.value = data.photos.length;
    }
  } catch (error) {
    console.error('Fehler beim Abrufen der ausstehenden Fan-Fotos:', error);
  }
};

// Fan-Fotos-Tab öffnen und zu ausstehenden Fotos navigieren
const goToPendingFanPhotos = () => {
  setMediaTab('fanphotos');
  // Hier könnte man zusätzlich den Filter auf "Noch nicht freigegeben" setzen, wenn die AdminFanPhotos-Komponente eine entsprechende Methode bereitstellt
};

// Fensterbreite prüfen und mobiles Menü schließen, wenn Fenster vergrößert wird
const checkWindowSize = () => {
  if (window.innerWidth > 576 && mobileMenuOpen.value) {
    mobileMenuOpen.value = false;
  }
};

// Theme-Wechsel beobachten
watch(darkMode, (newValue) => {
  if (newValue) {
    document.body.classList.add('dark-mode');
    localStorage.setItem('admin-theme', 'dark');
  } else {
    document.body.classList.remove('dark-mode');
    localStorage.setItem('admin-theme', 'light');
  }
});

// Dark Mode umschalten
const toggleDarkMode = () => {
  darkMode.value = !darkMode.value;
};

// Rechtliches-Menü umschalten
const toggleLegalMenu = () => {
  isLegalMenuOpen.value = !isLegalMenuOpen.value;
  if (isLegalMenuOpen.value) isMediaMenuOpen.value = false;
};

// Media-Menü umschalten
const toggleMediaMenu = () => {
  isMediaMenuOpen.value = !isMediaMenuOpen.value;
  if (isMediaMenuOpen.value) isLegalMenuOpen.value = false;
};

// Rechtlichen Unterpunkt setzen
const setLegalTab = (tab) => {
  legalSubTab.value = tab;
  activeTab.value = 'legal';
  closeMobileMenu();
};

// Media Unterpunkt setzen
const setMediaTab = (tab) => {
  mediaSubTab.value = tab;
  activeTab.value = 'media';
  closeMobileMenu();
};

// Tab wechseln und mobiles Menü schließen
const setTab = (tab) => {
  activeTab.value = tab;
  closeMobileMenu();
};

// Mobiles Menü umschalten
const toggleMobileMenu = () => {
  mobileMenuOpen.value = !mobileMenuOpen.value;
};

// Mobiles Menü schließen
const closeMobileMenu = () => {
  mobileMenuOpen.value = false;
};

const emit = defineEmits(['logout']);

const handleLogout = () => {
  emit('logout');
};
</script>

<template>
  <div class="admin-dashboard" :class="{ 'dark-theme': darkMode }">
    <header class="admin-header">
      <div class="container">
        <div class="header-content">
          <div class="logo">
            <div class="logo-icon">
              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12,2C9.1,2,6.8,4.3,6.8,7.2c0,1.7,0.8,3.3,2.1,4.3c-1.3,1-2.1,2.5-2.1,4.3c0,1.7,0.8,3.3,2.1,4.3C7.6,21,6.8,22.5,6.8,24h2.4c0-1.3,1.1-2.4,2.4-2.4c1.3,0,2.4,1.1,2.4,2.4h2.4c0-1.5-0.8-3-2.1-3.9c1.3-1,2.1-2.5,2.1-4.3c0-1.7-0.8-3.3-2.1-4.3c1.3-1,2.1-2.5,2.1-4.3C16.8,4.3,14.9,2,12,2z" fill="currentColor"/>
              </svg>
            </div>
            <h1>HomeWard Admin</h1>
          </div>
          
          <div class="user-actions">
            <div class="user-greeting">
              {{ greeting }}, {{ username }}
            </div>
            
            <!-- Benachrichtigung für ausstehende Fan-Fotos -->
            <button v-if="pendingFanPhotos > 0" @click="goToPendingFanPhotos" class="notification-btn" title="Ausstehende Fan-Fotos">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
              </svg>
              <span class="notification-badge">{{ pendingFanPhotos }}</span>
            </button>
            
            <button class="action-btn theme-toggle" @click="toggleDarkMode">
              <svg v-if="darkMode" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="5"/>
                <path d="M12 1v2M12 21v2M4.2 4.2l1.4 1.4M18.4 18.4l1.4 1.4M1 12h2M21 12h2M4.2 19.8l1.4-1.4M18.4 5.6l1.4-1.4"/>
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
              </svg>
            </button>
            
            <a href="/" class="action-btn" target="_blank">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M10 3H6a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h12a2 2 0 0 0 2-2V9L14 3z"/>
                <path d="M14 3v6h6"/>
                <path d="M12 18v-6"/>
                <path d="M8 18v-1"/>
                <path d="M16 18v-3"/>
              </svg>
            </a>
            
            <button class="action-btn logout-btn" @click="handleLogout">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>

            <!-- Mobile Menu Toggle Button - nur auf kleinen Bildschirmen sichtbar -->
            <button class="mobile-menu-toggle" :class="{ 'active': mobileMenuOpen }" @click="toggleMobileMenu" aria-label="Menü anzeigen">
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>
        </div>
      </div>
    </header>
    
    <div class="admin-layout">
      <!-- Die Sidebar, die auf mobilen Geräten zum Dropdown wird -->
      <aside class="admin-sidebar" :class="{ 'mobile-active': mobileMenuOpen }">
        <nav class="admin-nav">
          <button 
            @click="setTab('shows')" 
            :class="['nav-item', { active: activeTab === 'shows' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="16" y1="2" x2="16" y2="6"></line>
              <line x1="8" y1="2" x2="8" y2="6"></line>
              <line x1="3" y1="10" x2="21" y2="10"></line>
            </svg>
            Termine
          </button>
          <button 
            @click="setTab('about')" 
            :class="['nav-item', { active: activeTab === 'about' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            Über uns
          </button>
          
          <!-- Rechtliches Dropdown -->
          <div class="dropdown-menu">
            <button 
              @click="toggleLegalMenu" 
              :class="['nav-item', { active: activeTab === 'legal' }]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Rechtliches
              <svg class="dropdown-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline :class="{ rotated: isLegalMenuOpen }" points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div v-show="isLegalMenuOpen" class="dropdown-content">
              <button 
                @click="setLegalTab('imprint')" 
                :class="['nav-subitem', { active: activeTab === 'legal' && legalSubTab === 'imprint' }]"
              >
                Impressum
              </button>
              <button 
                @click="setLegalTab('privacy')" 
                :class="['nav-subitem', { active: activeTab === 'legal' && legalSubTab === 'privacy' }]"
              >
                Datenschutz
              </button>
            </div>
          </div>
          
          <!-- Media Dropdown mit Benachrichtigungsanzeige -->
          <div class="dropdown-menu">
            <button 
              @click="toggleMediaMenu" 
              :class="['nav-item', { active: activeTab === 'media' }]"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M2 12C2 6.48 6.48 2 12 2s10 4.48 10 10-4.48 10-10 10S2 17.52 2 12zm10 6c3.31 0 6-2.69 6-6s-2.69-6-6-6-6 2.69-6 6 2.69 6 6 6z"></path>
                <circle cx="12" cy="12" r="2"></circle>
              </svg>
              Media
              <svg class="dropdown-arrow" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <polyline :class="{ rotated: isMediaMenuOpen }" points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
            <div v-show="isMediaMenuOpen" class="dropdown-content">
              <button 
                @click="setMediaTab('music')" 
                :class="['nav-subitem', { active: activeTab === 'media' && mediaSubTab === 'music' }]"
              >
                Musik
              </button>
              <button 
                @click="setMediaTab('photos')" 
                :class="['nav-subitem', { active: activeTab === 'media' && mediaSubTab === 'photos' }]"
              >
                Fotos
              </button>
              <button 
                @click="setMediaTab('videos')" 
                :class="['nav-subitem', { active: activeTab === 'media' && mediaSubTab === 'videos' }]"
              >
                Videos
              </button>
              <button 
                @click="setMediaTab('fanphotos')" 
                :class="['nav-subitem', { active: activeTab === 'media' && mediaSubTab === 'fanphotos' }]"
              >
                Fan-Fotos
                <span v-if="pendingFanPhotos > 0" class="menu-badge">{{ pendingFanPhotos }}</span>
              </button>
              <button 
                @click="setMediaTab('qrcode')" 
                :class="['nav-subitem', { active: activeTab === 'media' && mediaSubTab === 'qrcode' }]"
              >
                Fan-Foto QR-Code
              </button>
            </div>
          </div>
          
          <button 
            @click="setTab('stats')" 
            :class="['nav-item', { active: activeTab === 'stats' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
              <line x1="2" y1="20" x2="22" y2="20"></line>
            </svg>
            Statistik
          </button>
          <button 
            @click="setTab('users')" 
            :class="['nav-item', { active: activeTab === 'users' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            Benutzer
          </button>
          <button 
            @click="setTab('maintenance')" 
            :class="['nav-item', { active: activeTab === 'maintenance' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path>
            </svg>
            Wartungsseite
          </button>
          <button 
            @click="setTab('watermark')" 
            :class="['nav-item', { active: activeTab === 'watermark' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"></path>
            </svg>
            Wasserzeichen
          </button>
          <button 
            @click="setTab('settings')" 
            :class="['nav-item', { active: activeTab === 'settings' }]"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            Einstellungen
          </button>
        </nav>
      </aside>
      
      <main class="admin-content">
        <div v-if="activeTab === 'shows'">
          <ShowsManager />
        </div>
        
        <div v-else-if="activeTab === 'about'">
          <AboutManager />
        </div>
        
        <div v-else-if="activeTab === 'legal'">
          <ImprintManager v-if="legalSubTab === 'imprint'" />
          <PrivacyManager v-else-if="legalSubTab === 'privacy'" />
        </div>
        
        <div v-else-if="activeTab === 'media'">
          <MusicManager v-if="mediaSubTab === 'music'" />
          <PhotosManager v-else-if="mediaSubTab === 'photos'" />
          <VideoManager v-else-if="mediaSubTab === 'videos'" />
          <AdminFanPhotos v-else-if="mediaSubTab === 'fanphotos'" @photo-status-changed="fetchPendingFanPhotos" />
          <FanPhotoQRCode v-else-if="mediaSubTab === 'qrcode'" />
        </div>
        
        <div v-else-if="activeTab === 'stats'">
          <StatsManager />
        </div>
        
        <div v-else-if="activeTab === 'users'">
          <UsersManager />
        </div>
        
        <div v-else-if="activeTab === 'maintenance'">
          <MaintenanceManager />
        </div>
        
        <div v-else-if="activeTab === 'settings'">
          <SettingsManager />
        </div>

        <div v-else-if="activeTab === 'watermark'">
          <WatermarkManager />
        </div>
      </main>
    </div>
  </div>
</template>

<style scoped>
:root {
  /* Light theme (default) */
  --bg-color: #f8f9fa;
  --bg-alt: #f1f3f5;
  --white: #ffffff;
  --text-color: #333333;
  --text-muted: #6c757d;
  --primary-color: #00843d;
  --primary-dark: #006430;
  --danger-color: #e53935;
  --border-color: #e9ecef;
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.1);
}

/* Dark theme */
.dark-theme {
  --bg-color: #212529;
  --bg-alt: #1a1d20;
  --white: #2a2e33;
  --text-color: #e9ecef;
  --text-muted: #adb5bd;
  --primary-color: #4db380;
  --primary-dark: #3d8e66;
  --danger-color: #f44336;
  --border-color: #343a40;
  --shadow-sm: 0 2px 4px rgba(0, 0, 0, 0.3);
  --shadow-md: 0 4px 6px rgba(0, 0, 0, 0.4);
}

.admin-dashboard {
  min-height: 100vh;
  background-color: var(--bg-alt);
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;
}

.admin-header {
  height: 60px;
  display: flex;
  align-items: center;
  background-color: var(--white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
  transition: background-color 0.3s ease;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.logo-icon {
  width: 32px;
  height: 32px;
  color: var(--primary-color);
}

.logo h1 {
  font-family: 'Playfair Display', serif;
  font-size: 1.6rem;
  font-weight: 700;
  margin: 0;
  color: var(--primary-color);
}

.user-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  color: var(--text-color);
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
}

.action-btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
  color: var(--primary-color);
}

.dark-theme .action-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.logout-btn {
  color: var(--danger-color);
}

.logout-btn:hover {
  color: var(--danger-color);
  background-color: rgba(229, 57, 53, 0.1);
}

.dark-theme .logout-btn:hover {
  background-color: rgba(244, 67, 54, 0.15);
}

.theme-toggle {
  color: var(--text-muted);
}

/* Mobile Menü Toggle Button */
.mobile-menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: space-between;
  width: 24px;
  height: 18px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  position: relative;
  z-index: 200;
}

.mobile-menu-toggle span {
  display: block;
  width: 100%;
  height: 2px;
  background-color: var(--text-color);
  transition: transform 0.3s ease, opacity 0.3s ease, background-color 0.3s ease;
}

.mobile-menu-toggle.active span:nth-child(1) {
  transform: translateY(8px) rotate(45deg);
  background-color: var(--primary-color);
}

.mobile-menu-toggle.active span:nth-child(2) {
  opacity: 0;
}

.mobile-menu-toggle.active span:nth-child(3) {
  transform: translateY(-8px) rotate(-45deg);
  background-color: var(--primary-color);
}

.admin-layout {
  display: flex;
  min-height: calc(100vh - 60px);
}

.admin-sidebar {
  width: 260px;
  background-color: var(--white);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.05);
  padding: 2rem 0;
  flex-shrink: 0;
  overflow-y: auto;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.admin-nav {
  display: flex;
  flex-direction: column;
  padding: 0 1rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border: none;
  background-color: transparent;
  border-radius: var(--radius-sm);
  text-align: left;
  color: var(--text-color);
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  transition: all 0.3s ease;
}

.nav-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
  color: var(--primary-color);
}

.dark-theme .nav-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.nav-item.active {
  background-color: rgba(0, 132, 61, 0.1);
  color: var(--primary-color);
}

.dark-theme .nav-item.active {
  background-color: rgba(77, 179, 128, 0.15);
}

.dropdown-menu {
  position: relative;
}

.dropdown-arrow {
  margin-left: auto;
}

.dropdown-arrow polyline {
  transition: transform 0.3s ease;
}

.dropdown-arrow polyline.rotated {
  transform: rotate(-180deg);
  transform-origin: center;
}

.dropdown-content {
  margin-left: 2.5rem;
  margin-bottom: 0.5rem;
  display: flex;
  flex-direction: column;
  transition: max-height 0.3s ease;
}

.nav-subitem {
  padding: 0.6rem 1rem;
  text-align: left;
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 0.9rem;
  cursor: pointer;
  border-radius: var(--radius-sm);
  margin-bottom: 0.25rem;
  transition: all 0.3s ease;
}

.nav-subitem:hover {
  background-color: rgba(0, 0, 0, 0.03);
  color: var(--primary-color);
}

.dark-theme .nav-subitem:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.nav-subitem.active {
  background-color: rgba(0, 132, 61, 0.05);
  color: var(--primary-color);
}

.dark-theme .nav-subitem.active {
  background-color: rgba(77, 179, 128, 0.1);
}

.admin-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  background-color: var(--bg-alt);
  transition: background-color 0.3s ease;
}

.container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
}

.coming-soon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.empty-feature {
  text-align: center;
  max-width: 400px;
  padding: 3rem 2rem;
  color: var(--text-muted);
}

.empty-feature svg {
  margin-bottom: 1.5rem;
  opacity: 0.5;
}

.empty-feature h3 {
  margin-bottom: 1rem;
  color: var(--text-color);
}

/* Responsive Anpassungen */
@media (max-width: 992px) {
  .admin-layout {
    flex-direction: column;
  }
  
  .admin-sidebar {
    width: 100%;
    padding: 1rem 0;
  }
  
  .admin-nav {
    flex-direction: row;
    flex-wrap: wrap;
    padding: 0 1rem;
    justify-content: center;
    gap: 0.5rem;
  }
  
  .nav-item {
    padding: 0.5rem 1rem;
    margin-bottom: 0.25rem;
  }
  
  .dropdown-content {
    position: absolute;
    background-color: var(--white);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: var(--radius-sm);
    margin-left: 0;
    margin-top: 0.25rem;
    width: 200px;
    z-index: 10;
  }
}

@media (max-width: 768px) {
  .header-content {
    flex-direction: column;
    gap: 1rem;
    text-align: center;
  }
  
  .user-actions {
    width: 100%;
    justify-content: center;
  }
  
  .admin-nav {
    overflow-x: auto;
    flex-wrap: nowrap;
    justify-content: flex-start;
    padding-bottom: 0.5rem;
    scrollbar-width: thin;
    -webkit-overflow-scrolling: touch;
  }
  
  .admin-nav::-webkit-scrollbar {
    height: 4px;
  }
  
  .admin-nav::-webkit-scrollbar-thumb {
    background-color: var(--primary-color);
    border-radius: 4px;
  }
  
  .nav-item {
    white-space: nowrap;
    flex-shrink: 0;
  }
  
  .dropdown-menu {
    position: static;
  }
  
  .dropdown-content {
    position: absolute;
    top: 100%;
    left: 0;
    width: 100%;
    max-width: 100%;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    z-index: 20;
  }
}

@media (max-width: 576px) {
  .header-content {
    flex-direction: row;
    text-align: left;
    gap: 0;
  }
  
  .user-actions {
    width: auto;
    justify-content: flex-end;
  }

  .mobile-menu-toggle {
    display: flex;
  }

  .action-btn {
    display: none;
  }
  
  .action-btn.theme-toggle {
    display: flex; /* Theme-Toggle-Button immer anzeigen */
  }
  
  .admin-sidebar {
    position: fixed;
    top: 60px;
    left: 0;
    width: 100%;
    height: calc(100vh - 60px);
    z-index: 99;
    transform: translateX(-100%);
    padding: 1rem;
    box-shadow: none;
    border-top: 1px solid var(--border-color);
  }
  
  .admin-sidebar.mobile-active {
    transform: translateX(0);
  }
  
  .admin-nav {
    flex-direction: column;
    overflow-y: auto;
    height: 100%;
    padding: 0;
  }
  
  .nav-item {
    width: 100%;
  }
  
  .admin-content {
    padding: 1rem;
  }
  
  .logo h1 {
    font-size: 1.4rem;
  }
  
  .dropdown-content {
    position: static;
    width: auto;
    box-shadow: none;
    border-radius: 0;
    background-color: transparent;
    padding-left: 2rem;
    margin-bottom: 0.5rem;
  }
}

@media (max-width: 480px) {
  .nav-item {
    font-size: 0.9rem;
    padding: 0.5rem 0.75rem;
  }
  
  .nav-item svg {
    width: 16px;
    height: 16px;
  }
}

/* Neue Styles für Benachrichtigungen */
.notification-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background-color: transparent;
  color: var(--text-color);
  cursor: pointer;
  transition: all 0.3s ease;
}

.notification-btn:hover {
  background-color: rgba(0, 0, 0, 0.06);
  color: var(--primary-color);
}

.dark-theme .notification-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #f44336;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
}

.menu-badge {
  display: inline-block;
  background-color: #f44336;
  color: white;
  border-radius: 10px;
  padding: 0 6px;
  font-size: 11px;
  margin-left: 5px;
  min-width: 16px;
  text-align: center;
}

.user-greeting {
  font-size: 1.1rem;
  color: var(--text-color);
  font-weight: 500;
  margin-right: 1rem;
  display: flex;
  align-items: center;
}
</style> 